import { useState } from 'react';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../services/collateralApi';
import { MAX_OWNERS } from '../types/Inventory';
import type { InventoryOwner, InventoryType, LegalRegime } from '../types/Inventory';
import type { ErrorField } from '../types/ErrorField';

export default function useInventoryPageForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
    const [isCreating, setIsCreating] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [inventoryType, setInventoryType] = useState<InventoryType | ''>('');
    const [legalRegime, setLegalRegime] = useState<LegalRegime | ''>('');
    const [quantityDescription, setQuantityDescription] = useState('');
    const [custodyAddress, setCustodyAddress] = useState('');
    const [custodyCity, setCustodyCity] = useState('');
    const [custodyState, setCustodyState] = useState('');
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [registryOffice, setRegistryOffice] = useState('');
    const [registryState, setRegistryState] = useState('');
    const [trusteeName, setTrusteeName] = useState('');
    const [trusteeDocument, setTrusteeDocument] = useState('');
    const [certificateId, setCertificateId] = useState('');
    const [certificateDate, setCertificateDate] = useState('');
    const [certifiedValue, setCertifiedValue] = useState<number | ''>('');
    const [comments, setComments] = useState('');

    const [owners, setOwners] = useState<InventoryOwner[]>([]);

    const [ownerSearch, setOwnerSearch] = useState('');
    const [ownerResults, setOwnerResults] = useState<any[]>([]);
    const [isSearchingOwner, setIsSearchingOwner] = useState(false);
    const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);

    const searchOwner = async (query: string) => {
        if (!query || query.length < 3) {
            setOwnerResults([]);
            setShowOwnerDropdown(false);
            return;
        }
        setIsSearchingOwner(true);
        try {
            const [legalResults, individualResults] = await Promise.all([
                collateralApi.searchLegalPerson(query).catch(() => []),
                collateralApi.searchPerson(query).catch(() => []),
            ]);
            const normalized = [
                ...legalResults.map((p: any) => ({
                    personId: p.personId || p.uuid || p.id || '',
                    name: p.legalName || p.fantasyName || p.name || '',
                    document: (p.govTaxId || '').replace(/\D/g, ''),
                    documentFormatted: p.govTaxIdFormatted || p.govTaxId || '',
                    type: 'PJ',
                })),
                ...individualResults.map((p: any) => ({
                    personId: p.id || p.personId || '',
                    name: p.name || p.fullName || '',
                    document: (p.socialId || '').replace(/\D/g, ''),
                    documentFormatted: p.socialId || '',
                    type: 'PF',
                })),
            ];
            setOwnerResults(normalized);
            setShowOwnerDropdown(normalized.length > 0);
        } catch {
            setOwnerResults([]);
        } finally {
            setIsSearchingOwner(false);
        }
    };

    const handleSelectOwner = (person: any) => {
        addOwner({ personId: person.personId, name: person.name, document: person.document });
        setOwnerSearch('');
        setOwnerResults([]);
        setShowOwnerDropdown(false);
    };

    const isDuplicateOwner = (list: InventoryOwner[], owner: InventoryOwner) =>
        !!owner.personId && list.some(o => o.personId === owner.personId);

    const addOwner = (owner: InventoryOwner) => {
        if (owners.length >= MAX_OWNERS) {
            toast.error(`Máximo de ${MAX_OWNERS} proprietários permitido`);
            return;
        }
        if (isDuplicateOwner(owners, owner)) {
            toast.error('Proprietário já adicionado');
            return;
        }
        setOwners(prev =>
            prev.length >= MAX_OWNERS || isDuplicateOwner(prev, owner) ? prev : [...prev, owner]
        );
    };
    const removeOwner = (index: number) => setOwners(prev => prev.filter((_, i) => i !== index));

    const validate = (): ErrorField[] => {
        const errors: ErrorField[] = [];
        if (!description.trim()) {
            errors.push({ fieldName: 'description', message: 'Campo obrigatório' });
        }
        if (!inventoryType) {
            errors.push({ fieldName: 'inventoryType', message: 'Campo obrigatório' });
        }
        if (owners.length < 1) {
            errors.push({ fieldName: 'owners', message: 'Informe ao menos um proprietário' });
        }
        return errors;
    };

    const handleCreate = async () => {
        const validationErrors = validate();
        if (validationErrors.length > 0) {
            setErrorFields(validationErrors);
            toast.error('Há campos obrigatórios!');
            return;
        }
        setIsCreating(true);
        setErrorFields([]);
        try {
            await collateralApi.createInventory({
                description,
                inventory_type: inventoryType || undefined,
                legal_regime: legalRegime || undefined,
                quantity_description: quantityDescription || undefined,
                custody_address: custodyAddress || undefined,
                custody_city: custodyCity || undefined,
                custody_state: custodyState ? custodyState.toUpperCase() : undefined,
                registration_number: registrationNumber || undefined,
                registry_office: registryOffice || undefined,
                registry_state: registryState ? registryState.toUpperCase() : undefined,
                trustee_name: trusteeName || undefined,
                trustee_document: trusteeDocument || undefined,
                certificate_id: certificateId || undefined,
                certificate_date: certificateDate || undefined,
                certified_value: certifiedValue === '' ? undefined : certifiedValue,
                name: name || undefined,
                comments: comments || undefined,
                owners: owners.map(o => ({
                    person_id: o.personId,
                    name: o.name,
                    document: o.document,
                })),
            });
            onCreated();
            onClose();
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Há campos obrigatórios!');
                setErrorFields(e.data.errors);
            } else if (e instanceof ApiError && e.status === 409) {
                toast.error(e.data?.message || e.message);
            } else {
                toast.error(e?.message || 'Erro ao cadastrar estoque');
            }
        } finally {
            setIsCreating(false);
        }
    };

    return {
        isCreating, errorFields,
        name, setName,
        description, setDescription,
        inventoryType, setInventoryType,
        legalRegime, setLegalRegime,
        quantityDescription, setQuantityDescription,
        custodyAddress, setCustodyAddress,
        custodyCity, setCustodyCity,
        custodyState, setCustodyState,
        registrationNumber, setRegistrationNumber,
        registryOffice, setRegistryOffice,
        registryState, setRegistryState,
        trusteeName, setTrusteeName,
        trusteeDocument, setTrusteeDocument,
        certificateId, setCertificateId,
        certificateDate, setCertificateDate,
        certifiedValue, setCertifiedValue,
        comments, setComments,
        owners, addOwner, removeOwner,
        ownerSearch, setOwnerSearch,
        ownerResults, isSearchingOwner, showOwnerDropdown,
        searchOwner, handleSelectOwner,
        handleCreate,
    };
}
